<template>
  <div v-if="Object.keys(resultOrchestrator.writeIns).length > 0 && !allWriteInExcluded">
    <div class="alert alert-info">
      {{ $t("backend.e2e.voting_round_reports.show.mapping_in_progress") }}
    </div>

    <div class="accordion" id="write-ins-accord">
      <write-in-map-option
        v-for="writeInOption in writeInOptions"
        :key="writeInOption.id"
        :write-in-option="writeInOption"
        :write-in-mapping-options="writeInMappingOptions"
        :result-orchestrator="resultOrchestrator"
        :write-in-mapping="writeInMapping"
        :current-page="currentPage[writeInOption.reference]"
        :items-per-page="itemsPerPage"
        :option-titles="optionTitles"
        :selectable-options="getSelectableOptions(writeInOption.reference)"
        @navigate="(reference, page) => currentPage[reference] = page"
        @add-option="(reference: string, newOption: string) => addAsMappingOption(reference, newOption)"
        @add-global-option="(reference: string, newOption: string) => addAsGlobalOption(reference, newOption)"
        @update-mapping="(writeIn, value) => updateWriteIn(writeInOption.reference, writeIn, value)"
      />
    </div>

    <button class="btn btn-primary mt-3 me-2" @click="createMapping">
      {{ isMapped
        ? $t("backend.e2e.voting_round_reports.show.submit_mapping")
        : $t("backend.e2e.voting_round_reports.show.continue")
      }}
    </button>
    <button class="btn btn-outline-primary border-0 mt-3" @click="downloadWriteIns">
      {{ $t("backend.e2e.voting_round_reports.show.download_write_ins") }}
    </button>
  </div>
  <div v-else>
    <p v-if="allWriteInExcluded">
      {{ $t("backend.e2e.voting_round_reports.show.mapping_of_excluded_not_allowed") }}
    </p>
    <p v-else>
      {{ $t("backend.e2e.voting_round_reports.show.no_write_in_votes") }}
    </p>
    <button class="btn btn-primary" @click="createMapping">
      {{ $t("backend.e2e.voting_round_reports.show.continue") }}
    </button>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onBeforeMount } from "vue";
  import { flattenOptions } from "@/entrypoints/shared/contest_utilities";
  import { getMeaningfulLabel } from "@assemblyvoting/electa-ui";
  import apiClient from "@/entrypoints/frontend/apiClient";
  import WriteInMapOption from "./WriteInMapOption.vue";
  import i18n from "@/entrypoints/shared/i18n_backend";
  import type {
    ConferenceContest,
    ConferenceOption,
    PropType,
    ResultOrchestrator,
    MappableWriteInOption,
    WriteInMapping,
    OptionMappedWriteIns
  } from "@/types";

  const { t, locale } = i18n.global;

  const props = defineProps({
    resultOrchestrator: {
      type: Object as PropType<ResultOrchestrator>,
      required: true,
    },
    contest: {
      type: Object as PropType<ConferenceContest>,
      required: true,
    },
  });

  const writeInMapping = ref<WriteInMapping>({});

  const writeInMappingOptions = ref<OptionMappedWriteIns>({});

  const writeInCustomAndGlobalOptions = ref<OptionMappedWriteIns>({});

  const defaultMappingOptions = ref<MappableWriteInOption[]>([]);

  const globalOptions = ref<string[]>([]);

  const currentPage = ref<Record<string, number>>({});

  const itemsPerPage = ref(10);

  const flattenedOptions = computed(() => flattenOptions(props.contest.options));

  const optionTitles = computed(() => 
    Object.fromEntries(flattenedOptions.value.map(option => [
      option.reference,
      getMeaningfulLabel(
        option, locale, t("js.components.AVOption.aria_labels.option")) ||
      t("backend.e2e.voting_round_reports.show.invalid")
    ])
  ));

  const writeInOptions = computed(() => flattenedOptions.value.filter(option => !!option.writeIn));

  const nonWriteInOptions = computed(() => flattenedOptions.value.filter(option => !option.writeIn));

  const isMapped = computed(() => {
    for (const [_reference, mappings] of Object.entries(writeInMapping.value)) {
      for (const [key, value] of Object.entries(mappings)) {
        if (key !== value) {
          return true;
        }
      }
    }
    return false;
  });

  const allWriteInExcluded = computed(() => writeInOptions.value.every((option: ConferenceOption) => option.excluded));

  const getSelectableOptions = (optionReference: string) => {
    return [
      ...defaultMappingOptions.value, // Original options
      ...writeInCustomAndGlobalOptions.value[optionReference] // Newly created (and global)
    ];
  };

  const addAsGlobalOption = (_optionReference: string, newOptionReference: string) => {
    Object.keys(writeInMapping.value).forEach(reference => {
      const newOption = {
        label: newOptionReference,
        code: `$global_${newOptionReference}`
      };

      writeInMappingOptions.value[reference].push(newOption);
      writeInCustomAndGlobalOptions.value[reference].push(newOption);
    });

    globalOptions.value.push(`$global_${newOptionReference}`);
  };

  const addAsMappingOption = (optionReference: string, newOptionReference: string) => {
    const newOption = {
      label: newOptionReference,
      code: newOptionReference,
    };
    writeInMappingOptions.value[optionReference].push(newOption);
    writeInCustomAndGlobalOptions.value[optionReference].push(newOption);
  };

  const createMapping = () => {
    const url = `${window.location.href.split("/voting_rounds/")[0]}/e2e/result_orchestrators/${props.resultOrchestrator.id}/create_mapping`;
    const filteredMapping: Record<string, Record<string, string> | string[]> = {};

    // Filter out mappings that was unchanged
    Object.entries(writeInMapping.value).forEach(([reference, mappings]) => {
      filteredMapping[reference] = {};
      Object.entries(mappings).forEach(([key, value]) => {
        if (key !== value) filteredMapping[reference][key] = value;
      });
    });

    filteredMapping.globalOptions = globalOptions.value;

    apiClient.post(url, { write_in_mapping: filteredMapping });
  };

  const updateWriteIn = (optionReference: string, writeInRef: string, newValue: string) =>
    writeInMapping.value[optionReference][writeInRef] = newValue;

  const downloadWriteIns = () => {
    const url = `${window.location.href.split("/voting_rounds/")[0]}/e2e/result_orchestrators/${props.resultOrchestrator.id}/download_write_ins`;
    window.open(url, "_blank");
  };

  onBeforeMount(() => {
    writeInOptions.value.forEach(writeIn => {
      writeInMappingOptions.value[writeIn.reference] = [];
      writeInCustomAndGlobalOptions.value[writeIn.reference] = [];
      writeInMapping.value[writeIn.reference] = {};
      currentPage.value[writeIn.reference] = 1;
      props.resultOrchestrator.writeIns[writeIn.reference]?.forEach(rawWriteIn => {
        writeInMapping.value[writeIn.reference][rawWriteIn] = rawWriteIn;
        if (!writeIn.excluded) {
          if (!writeInMappingOptions.value[writeIn.reference].some((option: MappableWriteInOption) => option.code === rawWriteIn)) {
            writeInMappingOptions.value[writeIn.reference].push({ label: rawWriteIn, code: rawWriteIn });
          };
        };
      });
    });

    defaultMappingOptions.value.push({ code: "$ref_invalid", label: t("backend.e2e.voting_round_reports.show.mark_invalid") });
    nonWriteInOptions.value.forEach(option => {
      defaultMappingOptions.value.push({
        code: `$ref_${option.reference}`,
        label: `${Object.values(option.title)[0]} (${option.reference})`,
      });
    });
  });
</script>
